import React, { useMemo } from 'react';

interface DatePickerProps {
  value: string;                    // YYYY-MM-DD
  onChange: (date: string) => void;
  className?: string;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

function isLeapYear(y: number): boolean {
  return (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
}

function maxDay(year: number, month: number): number {
  if (month === 2 && isLeapYear(year)) return 29;
  return DAYS_IN_MONTH[month - 1] || 31;
}

function pad(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}

function todayStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function offsetDate(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

const DatePicker: React.FC<DatePickerProps> = ({ value, onChange, className = '' }) => {
  const currentYear = new Date().getFullYear();
  const years = useMemo(() => {
    const arr: number[] = [];
    for (let y = currentYear; y >= currentYear - 10; y--) arr.push(y);
    return arr;
  }, [currentYear]);

  const parts = value ? value.split('-') : [];
  const year = parts[0] ? parseInt(parts[0]) : currentYear;
  const month = parts[1] ? parseInt(parts[1]) : 1;
  const day = parts[2] ? parseInt(parts[2]) : 1;

  const maxD = maxDay(year, month);
  const days = useMemo(() => {
    const arr: number[] = [];
    for (let d = 1; d <= maxD; d++) arr.push(d);
    return arr;
  }, [maxD]);

  const update = (y: number, m: number, d: number) => {
    const clamped = Math.min(d, maxDay(y, m));
    onChange(`${y}-${pad(m)}-${pad(clamped)}`);
  };

  const inputClass = 'px-2 py-1.5 text-xs border border-[var(--card-border)] cgm-bg-primary cgm-text-primary rounded-none';
  const presetClass = 'text-[10px] px-2 py-1 border border-[var(--card-border)] cgm-bg-secondary cgm-text-secondary hover:cgm-text-primary rounded-none cursor-pointer';

  return (
    <div className={className}>
      <div className="flex gap-1 mb-1.5">
        <button type="button" onClick={() => onChange(todayStr())} className={presetClass}>Today</button>
        <button type="button" onClick={() => onChange(offsetDate(1))} className={presetClass}>Yesterday</button>
        <button type="button" onClick={() => onChange(offsetDate(7))} className={presetClass}>Last week</button>
      </div>

      <div className="flex gap-1">
        <select
          value={year}
          onChange={e => update(parseInt(e.target.value), month, day)}
          className={`flex-1 ${inputClass}`}
        >
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <select
          value={month}
          onChange={e => update(year, parseInt(e.target.value), day)}
          className={`flex-1 ${inputClass}`}
        >
          {MONTHS.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
        </select>
        <select
          value={Math.min(day, maxD)}
          onChange={e => update(year, month, parseInt(e.target.value))}
          className={`flex-1 ${inputClass}`}
        >
          {days.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
      </div>
    </div>
  );
};

export default DatePicker;
